import { useEffect, useMemo, useState } from "react";
import { Download, Printer, Search, Cpu } from "lucide-react";
import { api, download, errMsg, fmtTime } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/opl/Common";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function RulesPage() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/public/rules").then((r) => setD(r.data)); }, []);
  const list = useMemo(() => (d?.rules || []).filter((r) => !q || (r.title + " " + r.description).toLowerCase().includes(q.toLowerCase())), [d, q]);
  if (!d) return <Spinner />;
  const rb = d.rulebook;
  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14 print-area">
      <p className="eyebrow">Official document</p>
      <h1 className="text-4xl sm:text-5xl font-bold mt-2 text-[#0F172A]">Rules & Regulations</h1>
      <p className="text-slate-500 mt-3 text-sm" data-testid="rules-meta">
        {rb ? `Rulebook v${rb.version} · Published ${fmtTime(rb.published_at)}` : "The official rulebook has not been published yet"}
        {d.last_updated && ` · Rules last updated ${fmtTime(d.last_updated)}`}
      </p>
      <div className="flex flex-wrap gap-2 mt-6 no-print">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the rulebook" className="pl-10 h-11 rounded-full bg-white border-[#E7E5E0] text-[#0F172A]" data-testid="rules-search-input" />
        </div>
        <button onClick={() => window.print()} className="flex items-center gap-2 px-5 py-2 rounded-full border border-[#E7E5E0] bg-white text-sm font-medium text-[#0F172A] hover:border-amber-400 transition-colors" data-testid="rules-print-btn"><Printer className="h-4 w-4" />Print</button>
        {rb && <button onClick={() => download("/public/rulebook", `OPL_S6_Rulebook_v${rb.version}.pdf`).catch((e) => toast.error(errMsg(e)))} className="navy-btn flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold" data-testid="rules-download-pdf-btn"><Download className="h-4 w-4 text-amber-400" />Download PDF</button>}
      </div>
      <div className="mt-10 space-y-4" data-testid="rules-list">
        {list.map((r, i) => (
          <section key={r.id} className="bg-white border border-[#E7E5E0] rounded-2xl p-6 flex gap-5" id={r.key}>
            <span className="num shrink-0 h-10 w-10 rounded-xl bg-amber-50 border border-amber-200 grid place-items-center font-bold text-amber-700">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-4">
                <h2 className="text-xl font-semibold text-[#0F172A]">{r.title}</h2>
                {r.engine_enforced && <span className="shrink-0 flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700"><Cpu className="h-3 w-3" />Auto-enforced</span>}
              </div>
              <p className="text-slate-600 mt-2 whitespace-pre-line text-sm leading-relaxed">{r.description}</p>
              {Object.keys(r.parameters || {}).length > 0 && <p className="text-xs text-slate-400 mt-3">{Object.entries(r.parameters).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>}
            </div>
          </section>
        ))}
        {!list.length && <EmptyState title={d.rules.length ? "No matching rules" : "No published rules yet"} hint="Rules appear here once the organizers publish them." />}
      </div>
    </div>
  );
}
